import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { API, money, imgUrl } from "../../lib/storeApi";
import { Plus, Pencil, Trash2, LogOut, X } from "lucide-react";
import ImageUpload from "./ImageUpload";
import { AdminPromotions } from "./AdminPromotions";
import { AdminContent } from "./AdminContent";
import { AdminGallery } from "./AdminGallery";
import { AdminFinancing } from "./AdminFinancing";
import { LeadSourceStats, LeadSourceLine } from "./AdminLeadSources";
import { useBulkDelete, SelectAllBox, RowBox, RowDelete, DeleteBar } from "./BulkDelete";
import { AdminTrash } from "./AdminTrash";

const empty = { name: "", brand: "", vendor: "", sku: "", category: "televisions", price: 0, shipping_fee: 0, description: "", features: "", warranty: "", image_url: "", source_url: "", featured: false, in_stock: true, pricing_type: "cart" };
const emptyMfr = { name: "", categories: "", description: "", logo: "", domain: "", website: "", featured: false, active: true, display_order: 999 };
const emptyGuide = { title: "", description: "", file_url: "", image_url: "", active: true, display_order: 999 };
const emptyBlog = { title: "", slug: "", excerpt: "", content_html: "", cover_image: "", meta_title: "", meta_description: "", tags: "", author: "Set Up Home Theater", status: "published" };
const LEAD_BADGE = {
  Booking: "bg-amber-100 text-amber-700",
  Manufacturer: "bg-[#3b9dff]/15 text-[#2b8ae8]",
  Commercial: "bg-[#0a1f44]/10 text-[#0a1f44]",
  Service: "bg-teal-100 text-teal-700",
  General: "bg-slate-100 text-slate-600",
};

const VENDOR_OPTIONS = ["", "Mount-It!", "Monoprice", "Parts Express", "Vanco International", "Dow Technologies", "ADI"];

const PIPELINE = ["New", "Contacted", "Won", "Lost"];
const statusStyle = {
  New: "bg-slate-100 text-slate-700 border-slate-200",
  Contacted: "bg-blue-100 text-blue-700 border-blue-200",
  Won: "bg-green-100 text-green-700 border-green-200",
  Lost: "bg-red-100 text-red-700 border-red-200",
};

const StatusSelect = ({ value, onChange, testid }) => {
  const v = value || "New";
  return (
    <select
      value={v}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className={`px-2 py-1 rounded-full text-xs font-semibold border cursor-pointer focus:outline-none ${statusStyle[v] || statusStyle.New}`}
    >
      {PIPELINE.map((s) => <option key={s} value={s}>{s}</option>)}
    </select>
  );
};

const SITE_NAME = "Set Up Home Theater";
const SITE_DOMAIN = "setuphometheater.com";
const TITLE_PX = 600;
const DESC_PX = 920;

const slugify = (s) => (s || "").toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
const stripHtml = (html) => (html || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();

let _seoCanvas;
const pxWidth = (text, font) => {
  if (typeof document === "undefined") return 0;
  _seoCanvas = _seoCanvas || document.createElement("canvas");
  const ctx = _seoCanvas.getContext("2d");
  ctx.font = font;
  return Math.round(ctx.measureText(text || "").width);
};

const SeoMeter = ({ text, font, limit, kind }) => {
  const w = pxWidth(text, font);
  const pct = Math.min(100, Math.round((w / limit) * 100));
  const over = w > limit;
  const warn = w > limit * 0.62;
  return (
    <div className="mt-1.5">
      <div className="flex items-center gap-3">
        <div className="flex-1 h-1.5 rounded-full bg-slate-200 overflow-hidden">
          <div className={`h-full rounded-full transition-all ${over ? "bg-red-500" : warn ? "bg-amber-400" : "bg-green-500"}`} style={{ width: `${pct}%` }} />
        </div>
        <span className={`text-[11px] tabular-nums ${over ? "text-red-500 font-semibold" : "text-slate-400"}`}>{w} / {limit}px</span>
      </div>
      {over && <p className="text-[11px] text-red-500 font-medium mt-1">Too long — Google will likely cut this {kind} off. Trim to fit ~{limit}px.</p>}
    </div>
  );
};

const GooglePreview = ({ slug, title, desc }) => (
  <div className="border border-slate-200 rounded-lg p-4">
    <p className="text-[10px] font-bold tracking-[0.15em] text-slate-400 mb-2">GOOGLE PREVIEW</p>
    <div className="flex items-center gap-2 mb-1.5">
      <div className="w-6 h-6 rounded-full bg-[#0a1f44] text-white text-xs font-bold flex items-center justify-center shrink-0">S</div>
      <div className="leading-tight min-w-0">
        <div className="text-[13px] text-slate-700">{SITE_NAME}</div>
        <div className="text-[12px] text-slate-500 truncate">{SITE_DOMAIN} › blog › {slug || "your-post"}</div>
      </div>
    </div>
    <h3 className="text-[#1a0dab] text-lg leading-snug hover:underline cursor-pointer line-clamp-1">{title || "Your post title"}</h3>
    <p className="text-[13px] text-slate-600 line-clamp-2">{desc || "Your meta description will appear here."}</p>
  </div>
);

// Parses a Marblism-style front-matter block (title:/slug:/meta_description:/… inside an <h2>)
// out of pasted HTML, returning the extracted fields + the cleaned article body.
const FM_KEYS = ["title", "meta_title", "slug", "meta_description", "excerpt", "tags", "author", "cover_image", "keywords"];
const escapeRegex = (s) => String(s).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

function parseFrontmatter(html) {
  if (!html || !/<h2[^>]*>/i.test(html)) return { found: false };
  const re = /<h2[^>]*>([\s\S]*?)<\/h2>/gi;
  let m, block = null, inner = null;
  while ((m = re.exec(html)) !== null) {
    const cnt = FM_KEYS.filter((k) => new RegExp(`(^|\\n)\\s*${k}\\s*:`, "i").test(m[1])).length;
    if (cnt >= 2) { block = m[0]; inner = m[1]; break; }
  }
  if (!block) return { found: false };

  const fm = {};
  let cur = null;
  inner.split(/\r?\n/).forEach((raw) => {
    const line = raw.trim();
    if (!line) return;
    const km = line.match(/^([a-z_]+)\s*:\s*([\s\S]*)$/i);
    if (km && FM_KEYS.includes(km[1].toLowerCase())) {
      cur = km[1].toLowerCase();
      let val = km[2].trim();
      const href = val.match(/href=["']([^"']+)["']/i);
      val = href ? href[1] : val.replace(/<[^>]+>/g, "").trim();
      fm[cur] = val;
    } else if (cur) {
      fm[cur] = `${fm[cur]} ${line.replace(/<[^>]+>/g, "").trim()}`.trim();
    }
  });

  let content = html.replace(block, "");
  content = content.replace(/^\s*(?:<hr\s*\/?>\s*)+/i, "");

  let bodyH1 = "";
  const h1 = content.match(/<h1[^>]*>([\s\S]*?)<\/h1>/i);
  if (h1) { bodyH1 = h1[1].replace(/<[^>]+>/g, "").replace(/\s+/g, " ").trim(); content = content.replace(h1[0], ""); }

  if (fm.cover_image) {
    const esc = escapeRegex(fm.cover_image);
    content = content.replace(new RegExp(`<p>\\s*<img[^>]*src=["']${esc}["'][^>]*>\\s*</p>`, "i"), "");
    content = content.replace(new RegExp(`<img[^>]*src=["']${esc}["'][^>]*>`, "i"), "");
  }
  content = content.replace(/^\s+/, "");

  return {
    found: true,
    fields: {
      title: bodyH1 || fm.title || "",
      meta_title: fm.meta_title || (bodyH1 && fm.title ? fm.title : ""),
      slug: fm.slug || "",
      meta_description: fm.meta_description || "",
      excerpt: fm.excerpt || "",
      tags: fm.tags || "",
      author: fm.author || "",
      cover_image: fm.cover_image || "",
    },
    content,
  };
}

const linkify = (text = "") => text.split(/(https?:\/\/\S+)/g).map((part, i) =>
  /^https?:\/\//.test(part) ? <a key={i} href={part} target="_blank" rel="noopener noreferrer" className="text-[#3b9dff] underline break-all">{part.split("/").pop()}</a> : part);

const AdminDashboard = () => {
  const nav = useNavigate();
  const token = localStorage.getItem("sht_admin_token");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [leads, setLeads] = useState([]);
  const [guides, setGuides] = useState([]);
  const [editingGuide, setEditingGuide] = useState(null);
  const [guideForm, setGuideForm] = useState(emptyGuide);
  const [savingGuide, setSavingGuide] = useState(false);
  const [blogs, setBlogs] = useState([]);
  const [editingBlog, setEditingBlog] = useState(null);
  const [blogForm, setBlogForm] = useState(emptyBlog);
  const [savingBlog, setSavingBlog] = useState(false);
  const [fmDetected, setFmDetected] = useState(false);
  const [leadFilter, setLeadFilter] = useState("all");
  const [editingMfr, setEditingMfr] = useState(null);
  const [mfrForm, setMfrForm] = useState(emptyMfr);
  const [savingMfr, setSavingMfr] = useState(false);
  const [tab, setTab] = useState("products");
  const [cats, setCats] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importText, setImportText] = useState("");
  const [importing, setImporting] = useState(false);
  const auth = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
  const leadDel = useBulkDelete("leads", token, (ids) => setLeads((p) => p.filter((x) => !ids.includes(x.id))));
  const quoteDel = useBulkDelete("quotes", token, (ids) => setQuotes((p) => p.filter((x) => !ids.includes(x.id))));
  const shownLeads = leads.filter((q) => leadFilter === "all" || q.lead_type === leadFilter);

  const load = useCallback(async () => {
    const h = { Authorization: `Bearer ${token}` };
    const r = await fetch(`${API}/admin/products`, { headers: h });
    if (r.status === 401 || r.status === 403) { localStorage.removeItem("sht_admin_token"); nav("/admin/login"); return; }
    setProducts(await r.json());
    try {
      const [o, q] = await Promise.all([
        fetch(`${API}/admin/orders`, { headers: h }).then((x) => x.json()),
        fetch(`${API}/admin/quotes`, { headers: h }).then((x) => x.json()),
      ]);
      setOrders(Array.isArray(o) ? o : []);
      setQuotes(Array.isArray(q) ? q : []);
      const m = await fetch(`${API}/admin/manufacturers`, { headers: h }).then((x) => x.json());
      setManufacturers(Array.isArray(m) ? m : []);
      const l = await fetch(`${API}/admin/leads`, { headers: h }).then((x) => x.json());
      setLeads(Array.isArray(l) ? l : []);
      const g = await fetch(`${API}/admin/guides`, { headers: h }).then((x) => x.json());
      setGuides(Array.isArray(g) ? g : []);
      const bl = await fetch(`${API}/admin/blog`, { headers: h }).then((x) => x.json());
      setBlogs(Array.isArray(bl) ? bl : []);
    } catch (e) {}
  }, [token, nav]);

  useEffect(() => {
    if (!token) { nav("/admin/login"); return; }
    load();
    fetch(`${API}/store/categories`).then((r) => r.json()).then(setCats).catch(() => {});
  }, [token, nav, load]);

  const openNew = () => { setForm(empty); setEditing("new"); };
  const openEdit = (p) => { setForm({ ...p, features: (p.features || []).join(", ") }); setEditing(p.id); };
  const close = () => setEditing(null);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form, price: parseFloat(form.price) || 0, features: form.features ? form.features.split(",").map((s) => s.trim()).filter(Boolean) : [], specs: form.specs || {} };
    const isNew = editing === "new";
    const url = isNew ? `${API}/admin/products` : `${API}/admin/products/${editing}`;
    await fetch(url, { method: isNew ? "POST" : "PUT", headers: auth, body: JSON.stringify(payload) });
    setSaving(false); close(); load();
  };

  const del = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await fetch(`${API}/admin/products/${id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
    load();
  };

  const logout = () => { localStorage.removeItem("sht_admin_token"); nav("/admin/login"); };

  // ---- Manufacturers ----
  const openNewMfr = () => { setMfrForm(emptyMfr); setEditingMfr("new"); };
  const openEditMfr = (m) => {
    const catStr = (m.categories && m.categories.length ? m.categories : (m.category ? [m.category] : [])).join(", ");
    setMfrForm({ ...m, categories: catStr });
    setEditingMfr(m.id);
  };
  const closeMfr = () => setEditingMfr(null);
  const saveMfr = async (e) => {
    e.preventDefault();
    setSavingMfr(true);
    const catsArr = String(mfrForm.categories || "").split(",").map((s) => s.trim()).filter(Boolean);
    const payload = { ...mfrForm, categories: catsArr, category: catsArr[0] || "", display_order: parseInt(mfrForm.display_order) || 999 };
    const isNew = editingMfr === "new";
    const url = isNew ? `${API}/admin/manufacturers` : `${API}/admin/manufacturers/${editingMfr}`;
    await fetch(url, { method: isNew ? "POST" : "PUT", headers: auth, body: JSON.stringify(payload) });
    setSavingMfr(false); closeMfr(); load();
  };
  const delMfr = async (id) => {
    if (!window.confirm("Delete this manufacturer?")) return;
    await fetch(`${API}/admin/manufacturers/${id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
    load();
  };
  const toggleMfr = async (m, field) => {
    const updated = { ...m, [field]: !m[field] };
    setManufacturers((prev) => prev.map((x) => (x.id === m.id ? updated : x)));
    await fetch(`${API}/admin/manufacturers/${m.id}`, { method: "PUT", headers: auth, body: JSON.stringify(updated) });
  };

  // ---- Guides (lead magnets) ----
  const openNewGuide = () => { setGuideForm(emptyGuide); setEditingGuide("new"); };
  const openEditGuide = (g) => { setGuideForm({ ...g }); setEditingGuide(g.id); };
  const closeGuide = () => setEditingGuide(null);
  const saveGuide = async (e) => {
    e.preventDefault();
    setSavingGuide(true);
    const payload = { ...guideForm, display_order: parseInt(guideForm.display_order) || 999 };
    const isNew = editingGuide === "new";
    const url = isNew ? `${API}/admin/guides` : `${API}/admin/guides/${editingGuide}`;
    await fetch(url, { method: isNew ? "POST" : "PUT", headers: auth, body: JSON.stringify(payload) });
    setSavingGuide(false); closeGuide(); load();
  };
  const delGuide = async (id) => {
    if (!window.confirm("Delete this guide?")) return;
    await fetch(`${API}/admin/guides/${id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
    load();
  };
  const toggleGuideActive = async (g) => {
    const updated = { ...g, active: !g.active };
    setGuides((prev) => prev.map((x) => (x.id === g.id ? updated : x)));
    await fetch(`${API}/admin/guides/${g.id}`, { method: "PUT", headers: auth, body: JSON.stringify(updated) });
  };

  // ---- Blog posts ----
  const openNewBlog = () => { setBlogForm(emptyBlog); setFmDetected(false); setEditingBlog("new"); };
  const openEditBlog = (b) => { setBlogForm({ ...b, tags: (b.tags || []).join(", ") }); setFmDetected(false); setEditingBlog(b.id); };
  const closeBlog = () => { setEditingBlog(null); setFmDetected(false); };
  const onBlogContentChange = (value) => {
    const p = parseFrontmatter(value);
    if (!p.found) { setBlogForm((f) => ({ ...f, content_html: value })); return; }
    setFmDetected(true);
    setBlogForm((f) => ({
      ...f,
      content_html: p.content,
      title: p.fields.title || f.title,
      slug: p.fields.slug || f.slug,
      meta_title: p.fields.meta_title || f.meta_title,
      meta_description: p.fields.meta_description || f.meta_description,
      excerpt: p.fields.excerpt || f.excerpt,
      tags: p.fields.tags || f.tags,
      author: p.fields.author || f.author,
      cover_image: p.fields.cover_image || f.cover_image,
    }));
  };
  const saveBlog = async (e) => {
    e.preventDefault();
    setSavingBlog(true);
    const payload = { ...blogForm, tags: String(blogForm.tags || "").split(",").map((t) => t.trim()).filter(Boolean) };
    const isNew = editingBlog === "new";
    const url = isNew ? `${API}/admin/blog` : `${API}/admin/blog/${editingBlog}`;
    await fetch(url, { method: isNew ? "POST" : "PUT", headers: auth, body: JSON.stringify(payload) });
    setSavingBlog(false); closeBlog(); load();
  };
  const delBlog = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    await fetch(`${API}/admin/blog/${id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
    load();
  };

  // CSV: header row required. Columns: name,vendor,sku,category,price,brand,description,features,image_url,source_url,warranty,featured
  const parseCSV = (text) => {
    const lines = text.trim().split(/\r?\n/).filter(Boolean);
    if (lines.length < 2) return [];
    const split = (line) => {
      const out = []; let cur = ""; let inQ = false;
      for (let i = 0; i < line.length; i++) {
        const c = line[i];
        if (c === '"') { if (inQ && line[i + 1] === '"') { cur += '"'; i++; } else inQ = !inQ; }
        else if (c === "," && !inQ) { out.push(cur); cur = ""; }
        else cur += c;
      }
      out.push(cur); return out.map((s) => s.trim());
    };
    const headers = split(lines[0]).map((h) => h.toLowerCase());
    return lines.slice(1).map((ln) => {
      const cells = split(ln); const row = {};
      headers.forEach((h, i) => { row[h] = cells[i] || ""; });
      return {
        name: row.name || "", vendor: row.vendor || "", sku: row.sku || "",
        category: row.category || "accessories", price: parseFloat(row.price) || 0,
        brand: row.brand || "", description: row.description || "",
        features: row.features ? row.features.split(/[|;]/).map((s) => s.trim()).filter(Boolean) : [],
        image_url: row.image_url || "", source_url: row.source_url || "", warranty: row.warranty || "",
        featured: String(row.featured).toLowerCase() === "true", specs: {},
      };
    }).filter((r) => r.name);
  };

  const runImport = async () => {
    setImporting(true);
    try {
      const rows = parseCSV(importText);
      if (!rows.length) { alert("No valid rows found. Include a header row."); setImporting(false); return; }
      const res = await fetch(`${API}/admin/products/bulk`, { method: "POST", headers: auth, body: JSON.stringify(rows) });
      const d = await res.json();
      alert(`Imported ${d.created} products.`);
      setImportOpen(false); setImportText(""); load();
    } catch { alert("Import failed. Check the CSV format."); }
    setImporting(false);
  };

  const updateQuoteStatus = async (qid, status) => {
    setQuotes((prev) => prev.map((q) => (q.id === qid ? { ...q, pipeline_status: status } : q)));
    await fetch(`${API}/admin/quotes/${qid}/status`, { method: "PATCH", headers: auth, body: JSON.stringify({ status }) });
  };

  const updateOrderStatus = async (sid, status) => {
    setOrders((prev) => prev.map((o) => (o.session_id === sid ? { ...o, pipeline_status: status } : o)));
    await fetch(`${API}/admin/orders/${sid}/status`, { method: "PATCH", headers: auth, body: JSON.stringify({ status }) });
  };

  const updateLeadStatus = async (lid, status) => {
    setLeads((prev) => prev.map((l) => (l.id === lid ? { ...l, pipeline_status: status } : l)));
    await fetch(`${API}/admin/leads/${lid}/status`, { method: "PATCH", headers: auth, body: JSON.stringify({ status }) });
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      <header className="bg-[#0a1f44] text-white">
        <div className="max-w-[1200px] mx-auto px-5 h-16 flex items-center justify-between">
          <h1 className="font-extrabold">Admin, {tab.charAt(0).toUpperCase() + tab.slice(1)}</h1>
          <div className="flex items-center gap-3">
            {tab === "products" && <button onClick={() => setImportOpen(true)} data-testid="import-btn" className="bg-white/10 hover:bg-white/20 px-4 py-2 rounded-full text-sm font-semibold">Import CSV</button>}
            {tab === "products" && <button onClick={openNew} data-testid="new-product-btn" className="bg-[#3b9dff] hover:bg-[#2b8ae8] px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-1"><Plus className="w-4 h-4" /> New</button>}
            {tab === "manufacturers" && <button onClick={openNewMfr} data-testid="new-manufacturer-btn" className="bg-[#3b9dff] hover:bg-[#2b8ae8] px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-1"><Plus className="w-4 h-4" /> New</button>}
            {tab === "guides" && <button onClick={openNewGuide} data-testid="new-guide-btn" className="bg-[#3b9dff] hover:bg-[#2b8ae8] px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-1"><Plus className="w-4 h-4" /> New</button>}
            {tab === "blog" && <button onClick={openNewBlog} data-testid="new-blog-btn" className="bg-[#3b9dff] hover:bg-[#2b8ae8] px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-1"><Plus className="w-4 h-4" /> New</button>}
            <button onClick={logout} data-testid="logout-btn" className="text-slate-300 hover:text-white flex items-center gap-1 text-sm"><LogOut className="w-4 h-4" /> Logout</button>
          </div>
        </div>
      </header>

      <div className="max-w-[1200px] mx-auto px-5 py-8">
        <div className="flex flex-wrap gap-2 mb-5" data-testid="admin-tabs">
          {[["products", `Products (${products.length})`], ["orders", `Orders (${orders.length})`], ["quotes", `Quotes (${quotes.length})`], ["leads", `Leads (${leads.length})`], ["guides", `Guides (${guides.length})`], ["blog", `Blog (${blogs.length})`], ["manufacturers", `Manufacturers (${manufacturers.length})`], ["promotions", "Promotions"], ["content", "Site Content"], ["gallery", "Recent Installs"], ["financing", "Financing"], ["trash", "Trash"]].map(([k, lbl]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
              className={`px-4 py-2 rounded-full text-sm font-semibold ${tab === k ? "bg-[#0a1f44] text-white" : "bg-white border border-slate-200 text-slate-600"}`}>{lbl}</button>
          ))}
        </div>

        {tab === "products" && (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="products-table">
            <table className="w-full text-sm">
              <thead className="bg-slate-100 text-left text-slate-600"><tr><th className="px-4 py-3">Name</th><th className="px-4 py-3">Vendor</th><th className="px-4 py-3">SKU</th><th className="px-4 py-3">Category</th><th className="px-4 py-3">Price / Type</th><th className="px-4 py-3"></th></tr></thead>
              <tbody>
                {products.map((p) => (
                  <tr key={p.id} className="border-t border-slate-100" data-testid={`admin-row-${p.slug}`}>
                    <td className="px-4 py-3 font-medium text-[#0a1f44]">
                      <div className="flex items-center gap-3">
                        {(p.image_url || p.image)
                          ? <img src={imgUrl(p.image_url || p.image)} alt="" referrerPolicy="no-referrer" className="h-9 w-9 rounded object-contain bg-slate-50 border border-slate-100 shrink-0" data-testid={`admin-img-${p.slug}`} />
                          : <span className="h-9 w-9 rounded bg-red-50 border border-red-200 text-red-500 text-[9px] font-bold flex items-center justify-center text-center shrink-0" data-testid={`admin-noimg-${p.slug}`}>NO IMG</span>}
                        <span>{p.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{p.vendor || "—"}</td>
                    <td className="px-4 py-3 text-slate-500 text-xs">{p.sku || "—"}</td>
                    <td className="px-4 py-3 text-slate-600">{p.category}</td>
                    <td className="px-4 py-3">
                      {p.pricing_type === "quote"
                        ? <span className="px-2 py-1 rounded-full text-xs font-semibold bg-[#0a1f44]/10 text-[#0a1f44]">Quote Only</span>
                        : <span className="font-semibold">{money(p.price)}</span>}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button onClick={() => openEdit(p)} data-testid={`edit-${p.slug}`} className="text-slate-400 hover:text-[#3b9dff] mr-3"><Pencil className="w-4 h-4 inline" /></button>
                      <button onClick={() => del(p.id)} data-testid={`del-${p.slug}`} className="text-slate-400 hover:text-red-500"><Trash2 className="w-4 h-4 inline" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "orders" && (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="orders-table">
            {orders.length === 0 ? <p className="p-8 text-center text-slate-500">No orders yet.</p> : (
              <table className="w-full text-sm">
                <thead className="bg-slate-100 text-left text-slate-600"><tr><th className="px-4 py-3">Date</th><th className="px-4 py-3">Items</th><th className="px-4 py-3">Customer</th><th className="px-4 py-3">Amount</th><th className="px-4 py-3">Payment</th><th className="px-4 py-3">Status</th></tr></thead>
                <tbody>
                  {orders.map((o) => (
                    <tr key={o.session_id} className="border-t border-slate-100">
                      <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{o.created_at ? new Date(o.created_at).toLocaleDateString() : "—"}</td>
                      <td className="px-4 py-3 text-slate-600">{(o.items || []).map((i) => `${i.qty}× ${i.name}`).join(", ").slice(0, 70) || "—"}</td>
                      <td className="px-4 py-3 text-slate-600">{o.email || "—"}</td>
                      <td className="px-4 py-3 font-semibold">{money(o.amount || 0)}</td>
                      <td className="px-4 py-3"><span className={`px-2 py-1 rounded-full text-xs font-semibold ${o.payment_status === "paid" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>{o.payment_status}</span></td>
                      <td className="px-4 py-3"><StatusSelect value={o.pipeline_status} onChange={(s) => updateOrderStatus(o.session_id, s)} testid={`order-status-${o.session_id}`} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {tab === "quotes" && (
          <div data-testid="quotes-table">
          <DeleteBar bd={quoteDel} testid="quotes-delete" />
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            {quotes.length === 0 ? <p className="p-8 text-center text-slate-500">No quote requests yet.</p> : (
              <table className="w-full text-sm">
                <thead className="bg-slate-100 text-left text-slate-600"><tr><th className="pl-4 py-3 w-8"><SelectAllBox bd={quoteDel} ids={quotes.map((q) => q.id)} testid="quotes-select-all" /></th><th className="px-4 py-3">Date</th><th className="px-4 py-3">Name</th><th className="px-4 py-3">Brand</th><th className="px-4 py-3">Contact</th><th className="px-4 py-3">Message</th><th className="px-4 py-3">Status</th><th className="px-4 py-3"></th></tr></thead>
                <tbody>
                  {quotes.map((q) => (
                    <tr key={q.id} className="border-t border-slate-100 align-top" data-testid={`quote-row-${q.id}`}>
                      <td className="pl-4 py-3"><RowBox bd={quoteDel} id={q.id} testid={`quote-select-${q.id}`} /></td>
                      <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{q.created_at ? new Date(q.created_at).toLocaleDateString() : "—"}</td>
                      <td className="px-4 py-3 font-medium text-[#0a1f44]">{q.name}</td>
                      <td className="px-4 py-3 text-slate-600">{q.brand || "—"}</td>
                      <td className="px-4 py-3 text-slate-600">{q.email}<br />{q.phone}</td>
                      <td className="px-4 py-3 text-slate-600 max-w-xs whitespace-pre-line"><LeadSourceLine q={q} />{q.message}</td>
                      <td className="px-4 py-3"><StatusSelect value={q.pipeline_status} onChange={(s) => updateQuoteStatus(q.id, s)} testid={`quote-status-${q.id}`} /></td>
                      <td className="px-4 py-3"><RowDelete bd={quoteDel} id={q.id} testid={`quote-delete-${q.id}`} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          </div>
        )}

        {tab === "guides" && (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="guides-table">
            {guides.length === 0 ? <p className="p-8 text-center text-slate-500">No guides yet. Click “New” to add a downloadable lead-magnet guide.</p> : (
              <table className="w-full text-sm">
                <thead className="bg-slate-100 text-left text-slate-600"><tr><th className="px-4 py-3">Order</th><th className="px-4 py-3">Title</th><th className="px-4 py-3">File</th><th className="px-4 py-3">Active</th><th className="px-4 py-3"></th></tr></thead>
                <tbody>
                  {guides.map((g) => (
                    <tr key={g.id} className="border-t border-slate-100 align-top" data-testid={`admin-guide-${g.slug}`}>
                      <td className="px-4 py-3 text-slate-400">{g.display_order}</td>
                      <td className="px-4 py-3 font-medium text-[#0a1f44]">{g.title}<div className="text-xs text-slate-400 font-normal max-w-md line-clamp-2">{g.description}</div></td>
                      <td className="px-4 py-3">{g.file_url ? <a href={g.file_url.startsWith("http") ? g.file_url : `${process.env.REACT_APP_BACKEND_URL}${g.file_url}`} target="_blank" rel="noreferrer" className="text-[#3b9dff] font-semibold text-xs">View PDF</a> : <span className="text-xs text-red-500 font-semibold">No file</span>}</td>
                      <td className="px-4 py-3"><button onClick={() => toggleGuideActive(g)} data-testid={`guide-active-${g.slug}`} className={`px-2 py-1 rounded-full text-xs font-semibold ${g.active ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>{g.active ? "Active" : "Hidden"}</button></td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <button onClick={() => openEditGuide(g)} data-testid={`edit-guide-${g.slug}`} className="text-slate-400 hover:text-[#3b9dff] mr-3"><Pencil className="w-4 h-4 inline" /></button>
                        <button onClick={() => delGuide(g.id)} data-testid={`del-guide-${g.slug}`} className="text-slate-400 hover:text-red-500"><Trash2 className="w-4 h-4 inline" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}


        {tab === "promotions" && <AdminPromotions token={token} />}
        {tab === "content" && <AdminContent token={token} />}
        {tab === "gallery" && <AdminGallery token={token} />}
        {tab === "financing" && <AdminFinancing token={token} />}
        {tab === "trash" && <AdminTrash token={token} onRestored={load} />}
        {tab === "blog" && (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="blog-table">
            {blogs.length === 0 ? <p className="p-8 text-center text-slate-500">No posts yet. Click “New” to write one (paste HTML, SEO auto-fills).</p> : (
              <table className="w-full text-sm">
                <thead className="bg-slate-100 text-left text-slate-600"><tr><th className="px-4 py-3">Title</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Date</th><th className="px-4 py-3"></th></tr></thead>
                <tbody>
                  {blogs.map((b) => (
                    <tr key={b.id} className="border-t border-slate-100 align-top" data-testid={`admin-blog-${b.slug}`}>
                      <td className="px-4 py-3 font-medium text-[#0a1f44]">{b.title}<div className="text-xs text-slate-400 font-normal">/blog/{b.slug}</div></td>
                      <td className="px-4 py-3"><span className={`px-2 py-1 rounded-full text-xs font-semibold ${b.status === "published" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>{b.status}</span></td>
                      <td className="px-4 py-3 text-slate-500 text-xs">{(b.published_at || b.created_at || "").slice(0, 10)}</td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <a href={`/blog/${b.slug}`} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-[#3b9dff] mr-3 text-xs font-semibold">View</a>
                        <button onClick={() => openEditBlog(b)} data-testid={`edit-blog-${b.slug}`} className="text-slate-400 hover:text-[#3b9dff] mr-3"><Pencil className="w-4 h-4 inline" /></button>
                        <button onClick={() => delBlog(b.id)} data-testid={`del-blog-${b.slug}`} className="text-slate-400 hover:text-red-500"><Trash2 className="w-4 h-4 inline" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {tab === "leads" && (
          <div data-testid="leads-table">
            <LeadSourceStats items={[...leads, ...quotes]} token={token} />
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <span className="text-sm text-slate-500 mr-1">Filter:</span>
              {["all", "Booking", "Price Match", "Manufacturer", "Commercial", "Service", "General"].map((f) => {
                const count = f === "all" ? leads.length : leads.filter((x) => x.lead_type === f).length;
                return (
                  <button
                    key={f}
                    onClick={() => { setLeadFilter(f); leadDel.toggleAll(leadDel.sel); }}
                    data-testid={`lead-filter-${f.toLowerCase()}`}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${leadFilter === f ? "bg-[#3b9dff] text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"}`}
                  >
                    {f === "all" ? "All" : f} ({count})
                  </button>
                );
              })}
            </div>
            <DeleteBar bd={leadDel} testid="leads-delete" />
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              {shownLeads.length === 0 ? (
                <p className="p-8 text-center text-slate-500">No leads in this view.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-slate-100 text-left text-slate-600"><tr><th className="pl-4 py-3 w-8"><SelectAllBox bd={leadDel} ids={shownLeads.map((q) => q.id)} testid="leads-select-all" /></th><th className="px-4 py-3">Date</th><th className="px-4 py-3">Type</th><th className="px-4 py-3">Name</th><th className="px-4 py-3">Contact</th><th className="px-4 py-3">Details</th><th className="px-4 py-3">Status</th><th className="px-4 py-3"></th></tr></thead>
                  <tbody>
                    {shownLeads.map((q) => (
                      <tr key={q.id} className="border-t border-slate-100 align-top" data-testid={`lead-row-${q.id}`}>
                        <td className="pl-4 py-3"><RowBox bd={leadDel} id={q.id} testid={`lead-select-${q.id}`} /></td>
                        <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{q.created_at ? new Date(q.created_at).toLocaleDateString() : "—"}</td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${LEAD_BADGE[q.lead_type] || "bg-slate-100 text-slate-600"}`} data-testid={`lead-tag-${q.id}`}>
                            {q.lead_type === "Manufacturer" && q.brand ? q.brand : q.lead_type}
                          </span>
                        </td>
                        <td className="px-4 py-3 font-medium text-[#0a1f44]">{q.name}</td>
                        <td className="px-4 py-3 text-slate-600">{q.email}<br />{q.phone}</td>
                        <td className="px-4 py-3 text-slate-600 max-w-md">
                          {q.service ? <div className="text-xs text-slate-400 mb-0.5">Service: {q.service}</div> : null}
                          <LeadSourceLine q={q} />
                          {q.business_name ? <div className="text-xs text-slate-400 mb-0.5">Business: {q.business_name}{q.project_type ? ` · ${q.project_type}` : ""}{q.num_units ? ` · ${q.num_units} rooms/displays` : ""}</div> : null}
                          <span className="whitespace-pre-line">{linkify(q.message)}</span>
                        </td>
                        <td className="px-4 py-3"><StatusSelect value={q.pipeline_status} onChange={(s) => updateLeadStatus(q.id, s)} testid={`lead-status-${q.id}`} /></td>
                        <td className="px-4 py-3"><RowDelete bd={leadDel} id={q.id} testid={`lead-delete-${q.id}`} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}
      </div>
        <div className={`max-w-[1200px] mx-auto px-5 -mt-4 pb-8 ${tab === "manufacturers" ? "" : "hidden"}`}>
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="manufacturers-table-admin">
            <table className="w-full text-sm">
              <thead className="bg-slate-100 text-left text-slate-600"><tr><th className="px-4 py-3">Order</th><th className="px-4 py-3">Name</th><th className="px-4 py-3">Category</th><th className="px-4 py-3">Logo</th><th className="px-4 py-3">Featured</th><th className="px-4 py-3">Active</th><th className="px-4 py-3"></th></tr></thead>
              <tbody>
                {manufacturers.map((m) => (
                  <tr key={m.id} className="border-t border-slate-100" data-testid={`admin-mfr-${m.slug}`}>
                    <td className="px-4 py-3 text-slate-400">{m.display_order}</td>
                    <td className="px-4 py-3 font-medium text-[#0a1f44]">{m.name}</td>
                    <td className="px-4 py-3 text-slate-600">{(m.categories && m.categories.length ? m.categories.join(", ") : m.category) || "—"}</td>
                    <td className="px-4 py-3">
                      {m.logo
                        ? <div className="flex items-center gap-2"><img src={imgUrl(m.logo)} alt="" referrerPolicy="no-referrer" className="h-8 max-w-[80px] object-contain" /><span className="text-[10px] font-semibold text-green-600">Custom</span></div>
                        : m.domain
                          ? <span className="text-xs text-amber-600 font-medium">Auto (logo)</span>
                          : <span className="text-xs font-semibold text-red-500">No image</span>}
                    </td>
                    <td className="px-4 py-3"><button onClick={() => toggleMfr(m, "featured")} data-testid={`mfr-featured-${m.slug}`} className={`px-2 py-1 rounded-full text-xs font-semibold ${m.featured ? "bg-[#3b9dff]/15 text-[#2b8ae8]" : "bg-slate-100 text-slate-500"}`}>{m.featured ? "Featured" : "No"}</button></td>
                    <td className="px-4 py-3"><button onClick={() => toggleMfr(m, "active")} data-testid={`mfr-active-${m.slug}`} className={`px-2 py-1 rounded-full text-xs font-semibold ${m.active ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>{m.active ? "Active" : "Hidden"}</button></td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button onClick={() => openEditMfr(m)} data-testid={`edit-mfr-${m.slug}`} className="text-slate-400 hover:text-[#3b9dff] mr-3"><Pencil className="w-4 h-4 inline" /></button>
                      <button onClick={() => delMfr(m.id)} data-testid={`del-mfr-${m.slug}`} className="text-slate-400 hover:text-red-500"><Trash2 className="w-4 h-4 inline" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

      {editingMfr && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="manufacturer-modal">
          <form onSubmit={saveMfr} className="bg-white rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4"><h2 className="text-lg font-bold text-[#0a1f44]">{editingMfr === "new" ? "New Manufacturer" : "Edit Manufacturer"}</h2><button type="button" onClick={closeMfr}><X className="w-5 h-5 text-slate-400" /></button></div>
            <label className="text-xs font-semibold text-slate-500">Name</label>
            <input required value={mfrForm.name} onChange={(e) => setMfrForm({ ...mfrForm, name: e.target.value })} data-testid="mf-name" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm mb-3 mt-1" />
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-semibold text-slate-500">Categories (comma-separated)</label><input value={mfrForm.categories} onChange={(e) => setMfrForm({ ...mfrForm, categories: e.target.value })} data-testid="mf-category" placeholder="e.g. Networking, Control & IoT Automation" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm mt-1" /></div>
              <div><label className="text-xs font-semibold text-slate-500">Display order</label><input type="number" value={mfrForm.display_order} onChange={(e) => setMfrForm({ ...mfrForm, display_order: e.target.value })} data-testid="mf-order" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm mt-1" /></div>
            </div>
            <ImageUpload value={mfrForm.logo} onChange={(v) => setMfrForm({ ...mfrForm, logo: v })} label="Logo (upload or paste URL — blank uses Brandfetch)" testid="mf-logo" />
            <label className="text-xs font-semibold text-slate-500">Domain (for Brandfetch logo fallback)</label>
            <input value={mfrForm.domain || ""} onChange={(e) => setMfrForm({ ...mfrForm, domain: e.target.value })} data-testid="mf-domain" placeholder="e.g. sonos.com" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm mt-1 mb-3" />
            <label className="text-xs font-semibold text-slate-500">Website</label>
            <input value={mfrForm.website} onChange={(e) => setMfrForm({ ...mfrForm, website: e.target.value })} data-testid="mf-website" placeholder="https://…" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm mt-1 mb-3" />
            <label className="text-xs font-semibold text-slate-500">Short description</label>
            <textarea rows="2" value={mfrForm.description} onChange={(e) => setMfrForm({ ...mfrForm, description: e.target.value })} data-testid="mf-desc" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm mt-1 mb-3" />
            <div className="flex items-center gap-6 mb-4">
              <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={mfrForm.featured} onChange={(e) => setMfrForm({ ...mfrForm, featured: e.target.checked })} data-testid="mf-featured" className="w-4 h-4 accent-[#3b9dff]" /> Featured</label>
              <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={mfrForm.active} onChange={(e) => setMfrForm({ ...mfrForm, active: e.target.checked })} data-testid="mf-activechk" className="w-4 h-4 accent-[#3b9dff]" /> Active (visible)</label>
            </div>
            <button disabled={savingMfr} data-testid="save-manufacturer-btn" className="w-full bg-[#3b9dff] hover:bg-[#2b8ae8] text-white py-2.5 rounded-full font-semibold disabled:opacity-60">{savingMfr ? "Saving…" : "Save Manufacturer"}</button>
          </form>
        </div>
      )}
      {editing && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="product-modal">
          <form onSubmit={save} className="bg-white rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4"><h2 className="text-lg font-bold text-[#0a1f44]">{editing === "new" ? "New Product" : "Edit Product"}</h2><button type="button" onClick={close}><X className="w-5 h-5 text-slate-400" /></button></div>
            <label className="text-xs font-semibold text-slate-500">Name</label>
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="f-name" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm mb-3 mt-1" />
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-semibold text-slate-500">Brand</label><input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} data-testid="f-brand" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm mt-1" /></div>
              <div><label className="text-xs font-semibold text-slate-500">Price ($) — 0 = quote only</label><input type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="f-price" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm mt-1" /></div>
              <div><label className="text-xs font-semibold text-slate-500">Shipping fee ($) — 0 = free / pickup</label><input type="number" step="0.01" value={form.shipping_fee} onChange={(e) => setForm({ ...form, shipping_fee: e.target.value })} data-testid="f-shipping" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm mt-1" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <div>
                <label className="text-xs font-semibold text-slate-500">Sales type</label>
                <select value={form.pricing_type || "cart"} onChange={(e) => setForm({ ...form, pricing_type: e.target.value })} data-testid="f-pricing-type" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm mt-1">
                  <option value="cart">Buy Online (cart & checkout)</option>
                  <option value="quote">Quote Only (hide price)</option>
                </select>
                <p className="text-[10px] text-slate-400 mt-1">Quote Only hides price and shows a Request-a-Quote button.</p>
              </div>
              <div><label className="text-xs font-semibold text-slate-500">SKU / Model</label><input value={form.sku || ""} onChange={(e) => setForm({ ...form, sku: e.target.value })} data-testid="f-sku" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm mt-1" /></div>
            </div>
            <div className="mt-3">
              <label className="text-xs font-semibold text-slate-500">Vendor (internal)</label>
              <select value={form.vendor || ""} onChange={(e) => setForm({ ...form, vendor: e.target.value })} data-testid="f-vendor" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm mt-1">
                {VENDOR_OPTIONS.map((v) => <option key={v} value={v}>{v || "— none —"}</option>)}
              </select>
            </div>
            <ImageUpload value={form.image_url} onChange={(v) => setForm({ ...form, image_url: v })} label="Product Image" testid="f-image" />
            <label className="text-xs font-semibold text-slate-500 mt-3 block">Category</label>
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="f-category" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm mt-1 mb-3">
              {cats.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
            </select>
            <label className="text-xs font-semibold text-slate-500">Description</label>
            <textarea rows="2" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="f-desc" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm mt-1 mb-3" />
            <label className="text-xs font-semibold text-slate-500">Features (comma-separated)</label>
            <input value={form.features} onChange={(e) => setForm({ ...form, features: e.target.value })} data-testid="f-features" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm mt-1 mb-3" />
            <label className="text-xs font-semibold text-slate-500">Warranty</label>
            <input value={form.warranty} onChange={(e) => setForm({ ...form, warranty: e.target.value })} data-testid="f-warranty" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm mt-1 mb-3" />
            <label className="flex items-center gap-2 text-sm text-slate-600 mb-4"><input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} data-testid="f-featured" className="w-4 h-4 accent-[#3b9dff]" /> Featured on store homepage</label>
            <button disabled={saving} data-testid="save-product-btn" className="w-full bg-[#3b9dff] hover:bg-[#2b8ae8] text-white py-2.5 rounded-full font-semibold disabled:opacity-60">{saving ? "Saving…" : "Save Product"}</button>
          </form>
        </div>
      )}
      {importOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="import-modal">
          <div className="bg-white rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-3"><h2 className="text-lg font-bold text-[#0a1f44]">Bulk Import Products (CSV)</h2><button type="button" onClick={() => setImportOpen(false)}><X className="w-5 h-5 text-slate-400" /></button></div>
            <p className="text-xs text-slate-500 mb-2">Paste CSV with a header row. Columns: <code className="bg-slate-100 px-1 rounded">name,vendor,sku,category,price,brand,description,features,image_url,source_url,warranty,featured</code>. Use <code className="bg-slate-100 px-1 rounded">|</code> to separate features. Set price to 0 for quote-only items.</p>
            <textarea value={importText} onChange={(e) => setImportText(e.target.value)} data-testid="import-textarea" rows="12" placeholder={"name,vendor,sku,category,price\nMonoprice HDMI 10ft,Monoprice,MP-HDMI-10,cables,14.99"} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-xs font-mono mt-1 mb-3" />
            <button disabled={importing} onClick={runImport} data-testid="run-import-btn" className="w-full bg-[#3b9dff] hover:bg-[#2b8ae8] text-white py-2.5 rounded-full font-semibold disabled:opacity-60">{importing ? "Importing…" : "Import Products"}</button>
          </div>
        </div>
      )}
      {editingBlog && (() => {
        const liveSlug = blogForm.slug ? slugify(blogForm.slug) : slugify(blogForm.title);
        const autoExcerpt = (blogForm.excerpt || stripHtml(blogForm.content_html).slice(0, 160)).trim();
        const previewTitle = blogForm.meta_title || blogForm.title;
        const previewDesc = blogForm.meta_description || autoExcerpt;
        return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="blog-modal-admin">
          <form onSubmit={saveBlog} className="bg-white rounded-2xl p-6 w-full max-w-2xl max-h-[92vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4"><h2 className="text-lg font-bold text-[#0a1f44]">{editingBlog === "new" ? "New Post" : "Edit Post"}</h2><button type="button" onClick={closeBlog}><X className="w-5 h-5 text-slate-400" /></button></div>

            <label className="text-xs font-semibold text-slate-500">Title *</label>
            <input required value={blogForm.title} onChange={(e) => setBlogForm({ ...blogForm, title: e.target.value })} data-testid="b-title" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm mb-3 mt-1" />

            <div className="grid grid-cols-2 gap-3 mb-1">
              <div>
                <label className="text-xs font-semibold text-slate-500">Slug (URL)</label>
                <input value={blogForm.slug} onChange={(e) => setBlogForm({ ...blogForm, slug: e.target.value })} data-testid="b-slug" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm mt-1" placeholder="auto from title" />
              </div>
              <div><label className="text-xs font-semibold text-slate-500">Status</label><select value={blogForm.status} onChange={(e) => setBlogForm({ ...blogForm, status: e.target.value })} data-testid="b-status" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm mt-1"><option value="published">Published</option><option value="draft">Draft</option></select></div>
            </div>
            <p className="text-[11px] text-slate-400 mb-3" data-testid="b-slug-preview">/blog/<span className="text-slate-600 font-medium">{liveSlug || "your-post"}</span></p>

            <div className="grid grid-cols-2 gap-3 mb-3">
              <div><label className="text-xs font-semibold text-slate-500">Tags (comma-separated)</label><input value={blogForm.tags} onChange={(e) => setBlogForm({ ...blogForm, tags: e.target.value })} data-testid="b-tags" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm mt-1" placeholder="e.g. TV, Install" /></div>
              <div><label className="text-xs font-semibold text-slate-500">Author</label><input value={blogForm.author} onChange={(e) => setBlogForm({ ...blogForm, author: e.target.value })} data-testid="b-author" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm mt-1" /></div>
            </div>

            <label className="text-xs font-semibold text-slate-500">Excerpt (summary — blank = auto from content)</label>
            <textarea rows="2" value={blogForm.excerpt} onChange={(e) => setBlogForm({ ...blogForm, excerpt: e.target.value })} data-testid="b-excerpt" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm mt-1 mb-3" />

            <div className="border border-slate-200 rounded-xl p-4 mb-4">
              <p className="text-[11px] font-bold tracking-[0.12em] text-slate-400 mb-3">SEO / GOOGLE SNIPPET (OPTIONAL)</p>

              <label className="text-xs font-semibold text-slate-600">Meta title</label>
              <input value={blogForm.meta_title} onChange={(e) => setBlogForm({ ...blogForm, meta_title: e.target.value })} data-testid="b-metatitle" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm mt-1" placeholder="Defaults to the post title" />
              <SeoMeter text={previewTitle} font="20px Arial" limit={TITLE_PX} kind="title" />

              <label className="text-xs font-semibold text-slate-600 mt-3 block">Meta description</label>
              <textarea rows="2" value={blogForm.meta_description} onChange={(e) => setBlogForm({ ...blogForm, meta_description: e.target.value })} data-testid="b-metadesc" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm mt-1" placeholder="Defaults to the excerpt" />
              <SeoMeter text={previewDesc} font="14px Arial" limit={DESC_PX} kind="description" />

              <div className="mt-4">
                <GooglePreview slug={liveSlug} title={previewTitle} desc={previewDesc} />
              </div>
            </div>

            <ImageUpload value={blogForm.cover_image} onChange={(v) => setBlogForm({ ...blogForm, cover_image: v })} label="Cover image" testid="b-cover" />

            <label className="text-xs font-semibold text-slate-500 mt-3 block">Content — paste HTML *</label>
            <p className="text-[11px] text-slate-400 mt-0.5 mb-1">Paste your full HTML — if it has a <code className="bg-slate-100 px-1 rounded">title:</code> / <code className="bg-slate-100 px-1 rounded">slug:</code> / <code className="bg-slate-100 px-1 rounded">meta_description:</code> header, the fields above auto-fill.</p>
            {fmDetected && <p className="text-[11px] font-semibold text-green-600 mb-1" data-testid="b-fm-detected">✓ Metadata imported from pasted HTML — Title, Slug, Excerpt, Meta &amp; Tags were auto-filled.</p>}
            <textarea required rows="12" value={blogForm.content_html} onChange={(e) => onBlogContentChange(e.target.value)} data-testid="b-content" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-xs font-mono mt-1 mb-4" placeholder="<h2>Heading</h2><p>Your post…</p>" />

            <button disabled={savingBlog} data-testid="save-blog-btn" className="w-full bg-[#3b9dff] hover:bg-[#2b8ae8] text-white py-2.5 rounded-full font-semibold disabled:opacity-60">{savingBlog ? "Saving…" : "Save Post"}</button>
          </form>
        </div>
        );
      })()}
      {editingGuide && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="guide-modal-admin">
          <form onSubmit={saveGuide} className="bg-white rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4"><h2 className="text-lg font-bold text-[#0a1f44]">{editingGuide === "new" ? "New Guide" : "Edit Guide"}</h2><button type="button" onClick={closeGuide}><X className="w-5 h-5 text-slate-400" /></button></div>
            <label className="text-xs font-semibold text-slate-500">Title</label>
            <input required value={guideForm.title} onChange={(e) => setGuideForm({ ...guideForm, title: e.target.value })} data-testid="g-title" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm mb-3 mt-1" />
            <label className="text-xs font-semibold text-slate-500">Description</label>
            <textarea rows="3" value={guideForm.description} onChange={(e) => setGuideForm({ ...guideForm, description: e.target.value })} data-testid="g-desc" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm mt-1 mb-3" />
            <ImageUpload value={guideForm.file_url} onChange={(v) => setGuideForm({ ...guideForm, file_url: v })} label="Guide file (upload a PDF or paste a URL)" testid="g-file" accept="application/pdf" />
            <ImageUpload value={guideForm.image_url} onChange={(v) => setGuideForm({ ...guideForm, image_url: v })} label="Cover image (optional)" testid="g-image" />
            <label className="text-xs font-semibold text-slate-500">Display order</label>
            <input type="number" value={guideForm.display_order} onChange={(e) => setGuideForm({ ...guideForm, display_order: e.target.value })} data-testid="g-order" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm mt-1 mb-3" />
            <label className="flex items-center gap-2 text-sm text-slate-600 mb-4"><input type="checkbox" checked={guideForm.active} onChange={(e) => setGuideForm({ ...guideForm, active: e.target.checked })} data-testid="g-active" className="w-4 h-4 accent-[#3b9dff]" /> Active (visible on storefront)</label>
            <button disabled={savingGuide} data-testid="save-guide-btn" className="w-full bg-[#3b9dff] hover:bg-[#2b8ae8] text-white py-2.5 rounded-full font-semibold disabled:opacity-60">{savingGuide ? "Saving…" : "Save Guide"}</button>
          </form>
        </div>
      )}

    </div>
  );
};

export default AdminDashboard;
